import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAtlas } from "@/context/AtlasContext";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { StatusPill } from "@/components/shared/StatusPill";
import { toast } from "sonner";
import {
  Sparkles, Send, ArrowUpRight, Play, ShieldAlert, AlertTriangle, Loader2,
  Lightbulb, ChevronRight, Zap, Pin, PinOff, BookOpen, Copy, Trash2, X,
} from "lucide-react";
import { Bot } from "lucide-react";

export const SUGGESTIONS = [
  "What needs my attention today?",
  "Which clients need a follow-up this week?",
  "Which opportunities are at risk of slipping?",
  "What work is overdue across my boards?",
  "Show me the biggest operational risks this week.",
];

function renderInline(text) {
  const nodes = [];
  const regex = /(\*\*[^*]+\*\*|`[^`]+`)/g;
  let last = 0, m, k = 0;
  while ((m = regex.exec(text)) !== null) {
    if (m.index > last) nodes.push(text.slice(last, m.index));
    const tok = m[0];
    if (tok.startsWith("**")) nodes.push(<strong key={k++} className="font-semibold text-slate-900">{tok.slice(2, -2)}</strong>);
    else nodes.push(<code key={k++} className="text-[11px] bg-slate-100 text-slate-500 px-1 rounded">{tok.slice(1, -1)}</code>);
    last = m.index + tok.length;
  }
  if (last < text.length) nodes.push(text.slice(last));
  return nodes;
}

export function Markdown({ text }) {
  const lines = (text || "").split("\n");
  return (
    <div className="text-sm text-slate-700 leading-relaxed space-y-1">
      {lines.map((ln, i) => {
        if (!ln.trim()) return <div key={i} className="h-1.5" />;
        if (ln.startsWith("## ")) return <h4 key={i} className="font-heading font-semibold text-slate-900 text-base mt-1">{renderInline(ln.slice(3))}</h4>;
        if (ln.startsWith("### ")) return <h5 key={i} className="font-semibold text-slate-900 mt-1">{renderInline(ln.slice(4))}</h5>;
        if (/^[-*]\s/.test(ln)) return <div key={i} className="flex gap-2"><span className="text-blue-500 mt-0.5">•</span><span>{renderInline(ln.replace(/^[-*]\s/, ""))}</span></div>;
        return <p key={i}>{renderInline(ln)}</p>;
      })}
    </div>
  );
}

export function AtlasAI({ open, onOpenChange, pinned = false, onTogglePin }) {
  const { activeTenant, ctx } = useAtlas();
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [attention, setAttention] = useState(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (open) {
      api.get("/ai/attention").then((r) => setAttention(r.data)).catch(() => {});
    }
  }, [open, ctx?.tenant?.id]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, loading]);

  const go = (link) => { if (link) { if (!pinned) onOpenChange(false); navigate(link); } };

  const send = async (text) => {
    const q = (text ?? input).trim();
    if (!q || loading) return;
    setInput("");
    const history = messages.map((m) => ({ role: m.role, content: m.role === "assistant" ? m.answer : m.content }));
    setMessages((s) => [...s, { role: "user", content: q }]);
    setLoading(true);
    try {
      const { data } = await api.post("/ai/ask", { question: q, history });
      setMessages((s) => [...s, { role: "assistant", answer: data.answer, citations: data.citations || [], actions: data.suggested_actions || [], assigned_to: data.assigned_to || null }]);
    } catch (e) {
      setMessages((s) => [...s, { role: "assistant", answer: apiError(e, "AI is unavailable right now."), citations: [], actions: [] }]);
    } finally {
      setLoading(false);
    }
  };

  const runAction = async (a, mi, ai_) => {
    try {
      const params = a.kind === "delete_record" ? { ...a.params, confirm: true } : a.params;
      const { data } = await api.post("/ai/act", { kind: a.kind, params, label: a.label });
      toast.success(a.label || "Action completed");
      setMessages((s) => s.map((m, i) => i === mi ? { ...m, actions: m.actions.map((x, j) => j === ai_ ? { ...x, done: true, affected: data.affected, skipped: data.skipped } : x) } : m));
    } catch (e) { toast.error(apiError(e)); }
  };

  const saveKnowledge = async (m, mi) => {
    const body = (m.answer || "").trim(); if (!body) return;
    const title = ((body.split("\n").find((l) => l.trim()) || "Atlas insight").replace(/[#*`>-]/g, "").trim()).slice(0, 80);
    try {
      await api.post("/ai/act", { kind: "propose_knowledge", label: "Save as knowledge", params: { title, body, type: "lesson" } });
      toast.success("Saved to Knowledge — pending approval");
      setMessages((s) => s.map((x, i) => i === mi ? { ...x, saved: true } : x));
    } catch (e) { toast.error(apiError(e)); }
  };

  const panel = (
    <div className="flex flex-col h-full min-h-0 bg-slate-50" data-testid="atlas-ai-panel">
        {/* Header */}
        <div className="px-5 py-4 border-b border-slate-200 bg-slate-900 text-white flex-shrink-0">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center"><Sparkles className="w-5 h-5" /></div>
            <div className="flex-1 min-w-0">
              <div className="font-heading font-semibold flex items-center gap-2">Atlas</div>
              <div className="text-xs text-slate-400 truncate">Reasoning across {activeTenant?.name}{ctx?.support_mode ? " · support mode" : ""}</div>
            </div>
            {onTogglePin && <button onClick={onTogglePin} title={pinned ? "Unpin from side" : "Keep open on the side"} data-testid="atlas-pin-toggle" className="p-1.5 rounded-lg hover:bg-white/10 text-slate-300">{pinned ? <PinOff className="w-4 h-4" /> : <Pin className="w-4 h-4" />}</button>}
          </div>
        </div>

        {/* Body */}
        <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-4 bg-slate-50">
          {messages.length === 0 && (
            <>
              <AttentionCard attention={attention} onGo={go} onAsk={(q) => send(q)} />
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2 flex items-center gap-1.5"><Lightbulb className="w-3.5 h-3.5" /> Try asking</p>
                <div className="space-y-1.5">
                  {SUGGESTIONS.map((s) => (
                    <button key={s} onClick={() => send(s)} data-testid="ai-suggestion"
                      className="w-full text-left text-sm text-slate-700 bg-white border border-slate-200 rounded-lg px-3 py-2 hover:border-blue-300 hover:bg-blue-50/40 transition-colors flex items-center justify-between group">
                      {s}<ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-blue-500" />
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}

          {messages.map((m, mi) => m.role === "user" ? (
            <div key={mi} className="flex justify-end">
              <div className="bg-blue-600 text-white rounded-2xl rounded-br-sm px-3.5 py-2 text-sm max-w-[85%]">{m.content}</div>
            </div>
          ) : (
            <div key={mi} className="bg-white border border-slate-200 rounded-2xl rounded-bl-sm p-3.5 space-y-3">
              {m.assigned_to && <div className="text-[11px] rounded-lg px-2.5 py-1.5 flex items-center gap-1.5" style={{ background: "var(--ai-soft)" }} data-testid="ai-assigned-badge"><Bot className="w-3 h-3" style={{ color: "var(--ai)" }} /> Handed to <b>{m.assigned_to.name}</b>{m.assigned_to.status === "needs_approval" && <span className="text-amber-700 ml-1">· needs approval in Decisions</span>}</div>}
              <Markdown text={m.answer} />
              {m.citations?.length > 0 && (
                <div className="flex flex-wrap gap-1.5 pt-1">
                  {m.citations.map((c, ci) => (
                    <button key={ci} onClick={() => go(c.link)} disabled={!c.link} data-testid="ai-citation"
                      className="inline-flex items-center gap-1 text-[11px] bg-slate-100 hover:bg-blue-100 text-slate-600 hover:text-blue-700 px-2 py-0.5 rounded-full border border-slate-200 transition-colors disabled:opacity-60">
                      {c.label || c.type}{c.link && <ArrowUpRight className="w-3 h-3" />}
                    </button>
                  ))}
                </div>
              )}
              {m.actions?.length > 0 && (
                <div className="space-y-1.5 pt-1 border-t border-slate-100">
                  {m.actions.map((a, ai_) => <ActionRow key={ai_} a={a} onRun={() => runAction(a, mi, ai_)} onGo={go} />)}
                </div>
              )}
              <div className="pt-1 flex items-center gap-3">
                <button onClick={() => { navigator.clipboard?.writeText(m.answer || ""); toast.success("Copied to clipboard"); }} data-testid="ai-copy-message" className="inline-flex items-center gap-1 text-[11px] text-slate-400 hover:text-blue-600 transition-colors"><Copy className="w-3.5 h-3.5" /> Copy</button>
                <button onClick={() => saveKnowledge(m, mi)} disabled={m.saved} data-testid="ai-save-knowledge" className="inline-flex items-center gap-1 text-[11px] text-slate-400 hover:text-indigo-600 disabled:text-emerald-600 transition-colors"><BookOpen className="w-3.5 h-3.5" /> {m.saved ? "Saved to Knowledge" : "Save as knowledge"}</button>
              </div>
            </div>
          ))}

          {loading && <div className="flex items-center gap-2 text-sm text-slate-400"><Loader2 className="w-4 h-4 animate-spin" /> Atlas is reasoning across your applications…</div>}
        </div>

        {/* Input */}
        <div className="border-t border-slate-200 p-3 bg-white">
          <div className="flex gap-2 items-end">
            <Textarea value={input} onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder="Ask Atlas anything across your business…" className="min-h-[44px] max-h-32 resize-none" data-testid="ai-input" />
            <Button size="icon" onClick={() => send()} disabled={loading || !input.trim()} data-testid="ai-send"><Send className="w-4 h-4" /></Button>
          </div>
          <p className="text-[10px] text-slate-400 mt-1.5 text-center">Governed by your permissions · actions are audited</p>
        </div>
    </div>
  );
  if (pinned) return <div className="h-full w-full flex flex-col min-h-0 border-l" style={{ borderColor: "var(--line)" }}>{panel}</div>;
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-md p-0 flex flex-col gap-0">{panel}</SheetContent>
    </Sheet>
  );
}

export function NoPermissionRow({ label }) {
  return (
    <div className="flex items-start gap-2 text-sm bg-slate-50 border border-slate-200 rounded-lg px-3 py-2" data-testid="ai-action-noperm">
      <ShieldAlert className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
      <div><div className="text-slate-700 font-medium">{label}</div><div className="text-slate-500 text-xs">You do not have permission to run this in this company. Ask an admin.</div></div>
    </div>
  );
}

export function ActionRow({ a, onRun, onGo }) {
  const [confirming, setConfirming] = useState(false);
  if (a.kind === "navigate") {
    return <button onClick={() => onGo(a.link)} className="w-full flex items-center justify-between text-sm text-blue-700 bg-blue-50 rounded-lg px-3 py-2 hover:bg-blue-100" data-testid="ai-action-navigate">{a.label}<ArrowUpRight className="w-4 h-4" /></button>;
  }
  if (a.kind === "draft_followup") {
    return (
      <div className="bg-slate-50 rounded-lg px-3 py-2 text-sm" data-testid="ai-action-draft">
        <div className="flex items-center gap-1.5 text-slate-500 text-xs mb-1"><Zap className="w-3.5 h-3.5" /> Drafted follow-up</div>
        <div className="font-medium text-slate-800">{a.params?.title}</div>
        {a.params?.body && <div className="text-slate-600 text-xs mt-0.5">{a.params.body}</div>}
      </div>
    );
  }
  if (a.kind === "delete_record") {
    if (a.done) return <div className="flex items-center gap-2 text-sm bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-slate-500" data-testid="ai-action-deleted"><Trash2 className="w-4 h-4" /> {a.label || "Record"} — deleted</div>;
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2.5 space-y-2" data-testid="ai-action-delete">
        <div className="flex items-start gap-2"><ShieldAlert className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" /><div className="text-red-900 text-sm font-medium">{a.label || "Delete record"}</div></div>
        {!confirming ? (
          <button onClick={() => setConfirming(true)} data-testid="ai-action-delete-start" className="w-full text-sm font-medium text-red-700 bg-white border border-red-300 rounded-md px-3 py-1.5 hover:bg-red-100 transition-colors flex items-center justify-center gap-1.5"><Trash2 className="w-3.5 h-3.5" /> Delete…</button>
        ) : (
          <div className="space-y-1.5">
            <p className="text-xs text-red-700">This permanently deletes the record and can't be undone.</p>
            <div className="flex gap-2">
              <button onClick={onRun} data-testid="ai-action-delete-confirm" className="flex-1 text-sm font-semibold text-white bg-red-600 rounded-md px-3 py-1.5 hover:bg-red-700 transition-colors flex items-center justify-center gap-1.5"><Trash2 className="w-3.5 h-3.5" /> Confirm delete</button>
              <button onClick={() => setConfirming(false)} data-testid="ai-action-delete-cancel" className="text-sm text-slate-600 bg-white border border-slate-200 rounded-md px-3 py-1.5 hover:bg-slate-50 flex items-center gap-1"><X className="w-3.5 h-3.5" /> Cancel</button>
            </div>
          </div>
        )}
      </div>
    );
  }
  if (a.kind === "create_contacts") {
    const contacts = a.params?.contacts || [];
    const org = a.params?.organization_name;
    if (a.permitted === false) return <NoPermissionRow label={a.label || `Create ${contacts.length} contact(s)`} />;
    if (a.done) {
      const n = a.affected?.length || 0; const sk = a.skipped || [];
      return (
        <div className="bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm" data-testid="ai-action-contacts-done">
          <div className="text-emerald-700 font-medium">Created {n} contact{n === 1 ? "" : "s"}{org ? ` under ${org}` : ""}</div>
          {sk.length > 0 && <div className="text-amber-700 text-xs mt-1" data-testid="ai-contacts-skipped">{sk.length} flagged as duplicate and not created: {sk.map((x) => x.name || x.email).join(", ")}</div>}
        </div>
      );
    }
    return (
      <div className="bg-white border border-slate-200 rounded-lg px-3 py-2.5 space-y-2" data-testid="ai-action-contacts">
        <div className="text-sm font-medium text-slate-800">Create {contacts.length} contact{contacts.length === 1 ? "" : "s"}{org ? ` under ${org}` : ""}</div>
        <div className="space-y-0.5 max-h-40 overflow-y-auto">
          {contacts.map((c, i) => <div key={i} className="text-xs text-slate-600 flex items-center gap-1.5" data-testid="ai-contact-preview"><span className="font-medium text-slate-700">{`${c.first_name || ""} ${c.last_name || ""}`.trim() || "(no name)"}</span>{c.title && <span className="text-slate-400">· {c.title}</span>}{c.email && <span className="text-slate-400">· {c.email}</span>}</div>)}
        </div>
        <Button size="sm" onClick={onRun} data-testid="ai-action-run-button"><Play className="w-3.5 h-3.5 mr-1" /> Run</Button>
      </div>
    );
  }
  if (a.executable && a.permitted === false) {
    return <NoPermissionRow label={a.label} />;
  }
  if (a.sensitive || !a.executable) {
    return (
      <div className="flex items-start gap-2 text-sm bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid="ai-action-recommend">
        <ShieldAlert className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
        <div><div className="text-amber-900 font-medium">{a.label}</div><div className="text-amber-700 text-xs">Recommended — requires manual confirmation (sensitive action)</div></div>
      </div>
    );
  }
  return (
    <div className="flex items-center justify-between gap-2 bg-white border border-slate-200 rounded-lg px-3 py-2" data-testid="ai-action-run">
      <span className="text-sm text-slate-700">{a.label}</span>
      {a.done ? <span className="text-xs text-emerald-600 font-medium flex items-center gap-1">Done</span>
        : <Button size="sm" onClick={onRun} data-testid="ai-action-run-button"><Play className="w-3.5 h-3.5 mr-1" /> Run</Button>}
    </div>
  );
}

export function AttentionCard({ attention, onGo, onAsk }) {
  if (!attention) return null;
  const issues = attention.issues || [];
  const sevTone = { high: "danger", medium: "warning", low: "neutral" };
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="ai-attention-card">
      <div className="flex items-center gap-2 mb-1"><AlertTriangle className="w-4 h-4 text-amber-500" /><span className="font-heading font-semibold text-slate-900">What needs attention</span></div>
      <p className="text-xs text-slate-500 mb-3">{attention.summary}</p>
      {issues.length === 0 && <p className="text-sm text-slate-400">All clear — nothing urgent across your applications.</p>}
      <div className="space-y-2">
        {issues.slice(0, 6).map((it, i) => (
          <button key={i} onClick={() => onGo(it.link)} data-testid="ai-attention-issue"
            className="w-full text-left border border-slate-100 rounded-lg p-2.5 hover:border-blue-200 hover:bg-blue-50/30 transition-colors">
            <div className="flex items-center gap-2">
              <StatusPill value={it.severity} tone={sevTone[it.severity]} />
              <span className="text-[10px] uppercase tracking-wide text-slate-400">{it.app}</span>
            </div>
            <div className="text-sm font-medium text-slate-800 mt-1">{it.title}</div>
            <div className="text-xs text-slate-500">{it.detail}</div>
          </button>
        ))}
      </div>
      {issues.length > 0 && (
        <Button variant="outline" size="sm" className="w-full mt-3" onClick={() => onAsk("Create follow-up tasks for the issues you found and tell me what you recommend.")} data-testid="ai-attention-followup">
          <Sparkles className="w-3.5 h-3.5 mr-1.5" /> Draft follow-ups for these
        </Button>
      )}
    </div>
  );
}
